/**
 * Tech job screen — the next-action machine (TECH-JOB-SCREEN.md §2.1).
 *
 * Pure step engine: derives the workflow rail and the bottom bar's lifecycle
 * action from data the job screen already fetches. This is a RE-PRESENTATION
 * of the existing workflow — every action maps to an endpoint/route that
 * exists today (STATUS_FLOW advance, IVR record, backflow/fire-safety/WO
 * workflow pages, explicit Complete Job). No JSX so the derivation and the
 * de-dupe audit are unit-testable under the client vitest config.
 *
 * §3b correction: the sticky bottom button is the STANDARD job lifecycle
 * control, identical on every job type (On My Way → I've Arrived → Complete
 * Job). It never shows WO step actions — those render inline in their step
 * cards in the rail.
 */

// ─── Action union — each kind names an EXISTING behavior ────────────────────

export type StepAction =
    | { kind: "on-my-way"; label: string } // POST /api/jobs/:id/on-my-way
    | { kind: "start-work"; label: string } // POST /api/jobs/:id/status { in_progress }
    | { kind: "ivr-checkin"; label: string } // IVR dialog → POST /api/jobs/:id/ivr-checkin
    | { kind: "ivr-checkout"; label: string } // IVR dialog → POST /api/jobs/:id/ivr-checkout
    | { kind: "open-backflow"; label: string } // navigate /field/backflow/:id
    | { kind: "open-fire-safety"; label: string } // navigate /field/fire-safety/:id
    | { kind: "open-wo"; label: string } // navigate /field/work-order/:id
    | { kind: "complete-job"; label: string; enabled: boolean }; // wrap-up modal → POST status office_review

export type StepId = "get-there" | "ivr-checkin" | "do-work" | "complete-wo" | "ivr-checkout" | "wrap-up";

export interface WorkflowStep {
    id: StepId;
    /** 1-based position shown in the rail circle. */
    ordinal: number;
    title: string;
    state: "done" | "active" | "locked";
    /** Collapsed line for done steps ("Checked in 8:42 · conf #12345"). */
    keyFact: string | null;
    /** One-liner under the title for active/locked steps. */
    detail: string | null;
    /** The step's primary action — also the sticky button when active. */
    action: StepAction | null;
    /** Done steps stay tappable (jump back); locked steps never are. */
    tappable: boolean;
}

export interface StepEngineInput {
    status: string;
    isVendorWO: boolean;
    isBackflow: boolean;
    isFireSafety: boolean;
    isRepairJob: boolean;
    /** Vendor WO has an IVR line (config has_ivr or briefing ivr_phone). */
    hasIvr: boolean;
    workStartedAt: string | null;
    ivrCheckinAt: string | null;
    ivrCheckinConfirmation: string | null;
    ivrCheckoutAt: string | null;
    ivrCheckoutConfirmation: string | null;
    /** Review & Submit stamp — jobs.test_submitted_at. */
    testSubmittedAt: string | null;
    /** work_order_completions.submitted_at (vendor WOs). */
    woSubmittedAt: string | null;
    testableCount: number;
    /** Testable devices with a saved result OR a release disposition. */
    resolvedCount: number;
    repairCount: number;
    repairedCount: number;
    replaceCount: number;
    replacedCount: number;
    /** Injected so the engine stays pure (defaults to raw ISO). */
    formatTime?: (iso: string) => string;
    /**
     * Helper Mode v2: the viewer is a ride-along helper. Tech-only actions
     * (lifecycle, IVR, Complete Job) are stripped, and the input steps
     * (Do The Work / Complete Work Order) unlock regardless of the tech's
     * lifecycle progress — helpers input, techs finalize.
     */
    viewerIsHelper?: boolean;
    /**
     * Helper Mode v2 review gate: helper-entered test rows the tech hasn't
     * reviewed yet (edit-save or [Looks right]). Complete Job stays disabled
     * until this is 0.
     */
    helperUnreviewedCount?: number;
}

/** The sticky bottom bar's action — lifecycle only, never a WO step action. */
export type LifecycleAction =
    | { kind: "on-my-way"; label: string }
    | { kind: "start-work"; label: string }
    | { kind: "complete-job"; label: string; enabled: boolean };

export interface StepEngineOutput {
    steps: WorkflowStep[];
    activeStepId: StepId | null;
    /**
     * The sticky bottom button — the standard job lifecycle control,
     * identical for every job type. Null → show the passive footer line.
     */
    lifecycleAction: LifecycleAction | null;
    /** Footer text when there's nothing left to do on this screen. */
    footer: string | null;
    /** Parity with the legacy workflowComplete gate. */
    workflowComplete: boolean;
}

const PRE_EN_ROUTE = new Set(["scheduled", "pending", "dispatched", "confirmed"]);
const EN_ROUTE = new Set(["en_route", "arrived"]);
const POST_WORK = new Set(["office_review", "completed", "ready_for_invoice", "invoiced", "closed"]);

/**
 * The work is off the tech's hands (#796). Exported so surfaces OUTSIDE the
 * step engine — the parts card, most importantly — gate on the SAME set the
 * rail does, instead of each growing its own idea of "done". The parts card
 * had no status gate at all, which is how a completed job ended up showing a
 * live "I have it" button directly above "Job sent to the office — nothing to
 * do here."
 */
export function isPostWorkStatus(status: string | null | undefined): boolean {
    return POST_WORK.has(status ?? "");
}

export function buildWorkflowSteps(input: StepEngineInput): StepEngineOutput {
    const fmt = input.formatTime ?? ((iso: string) => iso);
    const status = input.status;

    const preEnRoute = PRE_EN_ROUTE.has(status);
    const enRoute = EN_ROUTE.has(status);
    const inProgress = status === "in_progress";
    const postWork = POST_WORK.has(status);
    // Statuses where the tech can still act on this screen. Terminal/parked
    // statuses (cannot_complete, held, cancelled) show state but no button.
    const actionable = preEnRoute || enRoute || inProgress;

    const isTrade = input.isBackflow || input.isFireSafety;

    // ── Completion facts (same math as the legacy workflowComplete gate) ──
    const testComplete = !isTrade || input.testableCount === 0 || input.resolvedCount >= input.testableCount;
    const reportSubmitted = !isTrade || input.testableCount === 0 || input.testSubmittedAt != null;
    const repairComplete = !input.isRepairJob || input.repairCount === 0 || input.repairedCount >= input.repairCount;
    const replaceComplete = input.replaceCount === 0 || input.replacedCount >= input.replaceCount;
    const woFormSubmitted = !input.isVendorWO || input.woSubmittedAt != null;
    const woIvrComplete = !input.hasIvr || (input.ivrCheckinAt != null && input.ivrCheckoutAt != null);
    const workflowComplete =
        testComplete && reportSubmitted && repairComplete && replaceComplete && woFormSubmitted && woIvrComplete;
    // Helper Mode v2 review gate: Complete Job stays locked while any
    // helper-entered test row awaits the tech's review.
    const helperReviewClear = (input.helperUnreviewedCount ?? 0) === 0;
    const completeJobEnabled = workflowComplete && helperReviewClear;

    // ── Step facts ──
    const getThereDone = inProgress || postWork || input.workStartedAt != null;
    const doWorkDone = testComplete && reportSubmitted && repairComplete && replaceComplete;
    const wrapUpDone = postWork;

    // ── Build the ordered step list (per-service-type variants) ──
    type Draft = Omit<WorkflowStep, "ordinal" | "state" | "tappable"> & { done: boolean };
    const drafts: Draft[] = [];

    drafts.push({
        id: "get-there",
        title: "Get There",
        done: getThereDone,
        keyFact: input.workStartedAt ? `Started ${fmt(input.workStartedAt)}` : getThereDone ? "On site" : null,
        detail: null, // the rail renders address/schedule/navigate inline
        action: preEnRoute ? { kind: "on-my-way", label: "On My Way" } : { kind: "start-work", label: "I've Arrived" },
    });

    if (input.isVendorWO && input.hasIvr) {
        drafts.push({
            id: "ivr-checkin",
            title: "Check In — IVR",
            done: input.ivrCheckinAt != null,
            keyFact: input.ivrCheckinAt
                ? `Checked in ${fmt(input.ivrCheckinAt)}${
                      input.ivrCheckinConfirmation ? ` · conf #${input.ivrCheckinConfirmation}` : ""
                  }`
                : null,
            detail: 'Call the vendor\'s automated check-in phone line (their "IVR") and record your check-in',
            action: { kind: "ivr-checkin", label: "IVR Check-In" },
        });
    }

    // "Do The Work" exists for trade jobs (backflow / fire safety) and for
    // generic jobs. Non-backflow vendor WOs do the work INSIDE the WO
    // completion workflow (device results live there), so the step merges
    // into Complete Work Order for them — same as the engine today.
    const tradeWorkStep = isTrade;
    const genericWorkStep = !isTrade && !input.isVendorWO;
    if (tradeWorkStep) {
        const deviceNoun = input.isFireSafety ? "device" : "backflow";
        // A replace job is never told to "test backflows" (2026-10-22).
        const replaceOnly =
            !input.isRepairJob && !input.isFireSafety && input.testableCount === 0 && input.replaceCount > 0;
        const counts =
            input.testableCount > 0
                ? `${input.resolvedCount} of ${input.testableCount} ${deviceNoun}${input.testableCount === 1 ? "" : "s"} resolved`
                : replaceOnly
                  ? `${input.replacedCount} of ${input.replaceCount} ${deviceNoun}${input.replaceCount === 1 ? "" : "s"} replaced`
                  : "Add and test the devices on site";
        const needsSubmit = testComplete && input.testableCount > 0 && !reportSubmitted;
        drafts.push({
            id: "do-work",
            title: "Do The Work",
            done: doWorkDone,
            keyFact: doWorkDone
                ? input.isRepairJob && input.repairCount > 0
                    ? `${input.repairedCount}/${input.repairCount} repaired · report submitted`
                    : `${input.resolvedCount}/${input.testableCount} resolved · report submitted`
                : null,
            detail: needsSubmit ? "All devices resolved — submit the report (Review & Submit)" : counts,
            action: input.isFireSafety
                ? { kind: "open-fire-safety", label: "Inspect Fire Safety" }
                : {
                      kind: "open-backflow",
                      label: input.isRepairJob
                          ? "Perform Backflow Repair"
                          : needsSubmit
                            ? "Review & Submit"
                            : replaceOnly
                              ? input.replaceCount === 1
                                  ? "Replace the Backflow"
                                  : "Replace Backflows"
                              : input.replaceCount > 0
                                ? input.repairCount > 0
                                    ? "Repair & Replace Backflows"
                                    : "Test & Replace Backflows"
                                : "Test Backflows",
                  },
        });
    } else if (genericWorkStep) {
        drafts.push({
            id: "do-work",
            title: "Do The Work",
            done: inProgress || postWork, // nothing trackable — never blocks
            keyFact: postWork ? "Work recorded" : null,
            detail: "Do the work · add photos and notes as you go",
            action: null,
        });
    }

    if (input.isVendorWO) {
        drafts.push({
            id: "complete-wo",
            title: "Complete Work Order",
            done: input.woSubmittedAt != null,
            keyFact: input.woSubmittedAt ? `Submitted ${fmt(input.woSubmittedAt)}` : null,
            detail: "Fill out the work order completion form",
            action: { kind: "open-wo", label: "Complete Work Order" },
        });
    }

    if (input.isVendorWO && input.hasIvr) {
        drafts.push({
            id: "ivr-checkout",
            title: "Check Out — IVR",
            done: input.ivrCheckoutAt != null,
            keyFact: input.ivrCheckoutAt
                ? `Checked out ${fmt(input.ivrCheckoutAt)}${
                      input.ivrCheckoutConfirmation ? ` · conf #${input.ivrCheckoutConfirmation}` : ""
                  }`
                : null,
            detail: 'Call the vendor\'s automated phone line (their "IVR") again before leaving the site',
            action: { kind: "ivr-checkout", label: "IVR Check-Out" },
        });
    }

    drafts.push({
        id: "wrap-up",
        title: "Wrap Up",
        done: wrapUpDone,
        keyFact: wrapUpDone ? "Sent to office review" : null,
        detail: "Signatures, summary, complete the job",
        action: { kind: "complete-job", label: "Complete Job", enabled: completeJobEnabled },
    });

    // ── States: first not-done step is active; everything after is locked ──
    let activeStepId: StepId | null = null;
    const steps: WorkflowStep[] = drafts.map((d, i) => {
        let state: WorkflowStep["state"];
        if (d.done) {
            state = "done";
        } else if (activeStepId === null && actionable) {
            state = "active";
            activeStepId = d.id;
        } else {
            state = "locked";
        }
        // Done steps stay tappable so techs can jump back (re-record IVR,
        // edit tests) — but only while the job is still actionable, and never
        // Get There (re-firing a status advance would be a server error).
        const doneTappable = state === "done" && d.action != null && actionable && d.id !== "get-there";
        return {
            id: d.id,
            ordinal: i + 1,
            title: d.title,
            state,
            keyFact: d.keyFact,
            detail: d.detail,
            action: d.action,
            tappable: state === "active" || doneTappable,
        };
    });

    // ── Helper Mode v2 (HELPER-MODE.md §V2): helpers input, techs finalize.
    // Strip tech-only actions from the rail, and unlock the INPUT steps
    // (Do The Work / Complete Work Order) so a helper can start entering
    // readings and WO answers without waiting on the tech's lifecycle taps.
    // Wrap Up stays the tech's — the helper sees an honest handoff line.
    if (input.viewerIsHelper) {
        const techOnly = new Set(["on-my-way", "start-work", "ivr-checkin", "ivr-checkout", "complete-job"]);
        for (const step of steps) {
            if (step.action && techOnly.has(step.action.kind)) step.action = null;
            if ((step.id === "do-work" || step.id === "complete-wo") && step.state === "locked" && actionable) {
                step.state = "active";
                step.tappable = true;
            }
            if (step.id === "wrap-up" && step.state !== "done") {
                step.detail = "Your tech reviews your entries, signs, and completes the job.";
                step.tappable = false;
            }
        }
    }

    // ── Sticky button = the standard job lifecycle, identical on every job
    // type (§3b). WO step actions live inline in their step cards, never here.
    // Helpers never get a lifecycle button — their bottom bar is Clock In/Out.
    let lifecycleAction: LifecycleAction | null = null;
    if (!input.viewerIsHelper) {
        if (preEnRoute) {
            lifecycleAction = { kind: "on-my-way", label: "On My Way" };
        } else if (enRoute) {
            lifecycleAction = { kind: "start-work", label: "I've Arrived" };
        } else if (inProgress) {
            lifecycleAction = { kind: "complete-job", label: "Complete Job", enabled: completeJobEnabled };
        }
    }

    let footer: string | null = null;
    if (!lifecycleAction && !input.viewerIsHelper) {
        footer = postWork
            ? "Job sent to the office — nothing to do here."
            : `Job ${status.replace(/_/g, " ")} — nothing to do here.`;
    }

    return { steps, activeStepId, lifecycleAction, footer, workflowComplete };
}

// ─── Device status chips (reference column) ──────────────────────────────────

export type DeviceChipStatus = "pass" | "fail" | "skipped" | "not_tested" | "entered";

export interface DeviceForChips {
    asset_id: string;
    assembly_type: string | null;
    parent_asset_id: string | null;
    disposition: string | null;
    required_action: string | null;
}

export interface ResultForChips {
    asset_id: string;
    /** backflow overall_result or fire-safety result: "pass" | "fail". */
    result: string;
}

/**
 * Live status per device: saved result wins, then its bypass parent's result
 * (bypass child), then a release disposition (skipped / could_not_test / …),
 * else not tested.
 */
export function deriveDeviceStatuses(
    devices: DeviceForChips[],
    results: ResultForChips[],
): Map<string, DeviceChipStatus> {
    const byAsset = new Map<string, string>();
    for (const r of results) byAsset.set(r.asset_id, r.result);

    const statuses = new Map<string, DeviceChipStatus>();
    for (const d of devices) {
        const own = byAsset.get(d.asset_id);
        // A bypass leg (parent_asset_id set) never gets its own result row —
        // its readings live on the detector parent's row, so it's tested
        // exactly when its parent is. Inherit from the parent link itself,
        // NOT from the parent's assembly_type string: imports routinely leave
        // that blank/mislabelled, which used to strand a tested bypass as
        // "not tested".
        const inherited = !own && d.parent_asset_id ? byAsset.get(d.parent_asset_id) : undefined;
        const effective = own ?? inherited;
        // One rule (lib/test-state.ts): only a pass or a fail is a result. A
        // couldn't-test, unfinished or not-known row falls through to the
        // release / not-tested chips, never a red FAIL.
        if (effective === "pass" || effective === "fail") {
            statuses.set(d.asset_id, effective);
        } else if ((d.disposition ?? null) != null) {
            statuses.set(d.asset_id, "skipped");
        } else {
            statuses.set(d.asset_id, "not_tested");
        }
    }
    return statuses;
}

/**
 * Helper Mode v2 honest state (HELPER-MODE.md §V2 item 3): on a helper's
 * screen a device with saved readings is never "complete" — it reads
 * ENTERED (waiting on the tech to review & sign). Pass/fail chips are the
 * tech's view; skipped / not-tested stay as-is for both.
 */
export function helperVisibleStatuses(statuses: Map<string, DeviceChipStatus>): Map<string, DeviceChipStatus> {
    const out = new Map<string, DeviceChipStatus>();
    for (const [assetId, status] of statuses) {
        out.set(assetId, status === "pass" || status === "fail" ? "entered" : status);
    }
    return out;
}

// ─── De-dupe audit model (TECH-JOB-SCREEN.md §3.5) ──────────────────────────
// Assembles every user-visible text fact the screen renders, keyed by home.
// The test asserts the briefing scope, the IVR phone, and each device render
// exactly once. Components render FROM this split: rail owns actions/IVR,
// reference owns knowledge.

export interface ScreenModelInput {
    engine: StepEngineOutput;
    /** IVR phone/pin — rendered ONLY inside the expanded active IVR step. */
    ivrPhone: string | null;
    ivrPin: string | null;
    /** Briefing scope — rendered ONLY in the reference column. */
    scopeOfWork: string | null;
    schedulingNotes: string | null;
    devices: Array<{ asset_id: string; label: string }>;
}

export interface ScreenModel {
    /** Strings the rail renders (titles, key facts, active step content). */
    rail: string[];
    /** Strings the reference column renders. */
    reference: string[];
}

export function assembleScreenModel(input: ScreenModelInput): ScreenModel {
    const rail: string[] = [];
    for (const s of input.engine.steps) {
        rail.push(s.title);
        if (s.state === "done" && s.keyFact) rail.push(s.keyFact);
        if (s.state !== "done" && s.detail) rail.push(s.detail);
        // IVR phone/PIN render only inside the EXPANDED (active) IVR step —
        // collapsed/locked IVR steps never repeat the number.
        if (s.state === "active" && (s.id === "ivr-checkin" || s.id === "ivr-checkout")) {
            if (input.ivrPhone) rail.push(input.ivrPhone);
            if (input.ivrPin) rail.push(`PIN ${input.ivrPin}`);
        }
        // The amber scheduling warning is mirrored in Get There while it can
        // still change behavior (spec §2.1) — and lives in the reference
        // column permanently. That mirror is the ONE sanctioned duplicate.
    }

    const reference: string[] = [];
    if (input.scopeOfWork) reference.push(input.scopeOfWork);
    if (input.schedulingNotes) reference.push(input.schedulingNotes);
    for (const d of input.devices) reference.push(d.label);
    return { rail, reference };
}
